// hooks/useSubscribe.ts
import { useEffect, useMemo, useRef } from 'react';
import { Filter, subscribeToFilteredChanges } from '../api/generic';

function useLatest<T>(value: T) {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

export type Subscribable<T> = T & { id?: string };

const useSubscribe = <T extends { id?: string }>(
  collectionName: string,
  onUpdate: (items: T[]) => void,
  onError: (error: Error) => void,
  sortFunction?: (a: T, b: T) => number,
  filters?: Filter<T>[],
  enabled: boolean = true,
  deps: any[] = [],
): void => {
  // Stable key for filter changes only (don’t stringify huge things in deps directly)
  const filterKey = useMemo(() => JSON.stringify(filters ?? []), [filters]);

  // Always use the latest callbacks/sort without retriggering the subscription effect
  const onUpdateRef = useLatest(onUpdate);
  const onErrorRef = useLatest(onError);
  const sortRef = useLatest(sortFunction);

  useEffect(() => {
    if (!enabled) return;

    const unsubscribe = subscribeToFilteredChanges<T>(
      collectionName,
      filters ?? [],
      (items) => {
        const sorted = sortRef.current ? [...items].sort(sortRef.current) : items;
        onUpdateRef.current(sorted);
      },
      (err) => onErrorRef.current(err),
    );

    return () => unsubscribe();
  }, [collectionName, filterKey, enabled, ...deps]);
};

export default useSubscribe;
